// Admin server functions for the Hidden Gems Library.
// All writes are admin-gated via requireSupabaseAuth + has_role('admin').

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(ctx: {
  supabase: import("@supabase/supabase-js").SupabaseClient;
  userId: string;
}) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error) throw new Error("Role check failed: " + error.message);
  if (!data) throw new Error("Forbidden");
}

/** Imports (upserts) the checked-in CSV into library_items. Admin-only. */
export const importLibraryGems = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { importGemsChunked } = await import("./library.server");
    return importGemsChunked();
  });

/** Rebuilds the compact search index — returns it for caching. Admin-only. */
export const rebuildLibraryIndex = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { buildSearchIndex } = await import("./library.server");
    return buildSearchIndex();
  });

/** Processes one batch of pending OG previews. Admin-only, resumable. */
export const enrichLibraryOg = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { limit?: number }) => data ?? {})
  .handler(async ({ data, context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { enrichOgBatch } = await import("./library-og.server");
    const limit = Math.min(Math.max(data?.limit ?? 40, 1), 60);
    return enrichOgBatch(limit);
  });

/** Preview-enrichment status counts. Admin-only. */
export const libraryOgStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { ogStats } = await import("./library-og.server");
    return ogStats();
  });

/** Resets failed preview rows back to pending. Admin-only. */
export const retryFailedLibraryOg = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { resetFailedOg } = await import("./library-og.server");
    return resetFailedOg();
  });

/** Converts one batch of stored originals into sized WebP renditions. Admin-only. */
export const optimizeLibraryOg = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { limit?: number }) => data ?? {})
  .handler(async ({ data, context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { optimizeStoredBatch } = await import("./library-og.server");
    const limit = Math.min(Math.max(data?.limit ?? 25, 1), 40);
    return optimizeStoredBatch(limit);
  });

/** Generates branded SVG cards for gems with no obtainable preview. Admin-only. */
export const generateLibraryFallbackCards = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { limit?: number }) => data ?? {})
  .handler(async ({ data, context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { generateFallbackCards } = await import("./library-og.server");
    const limit = Math.min(Math.max(data?.limit ?? 200, 1), 500);
    return generateFallbackCards(limit);
  });




type LibraryItemRow = {
  id: string;
  ext_id: string;
  slug: string;
  category: string;
  subcategory: string;
  name: string;
  description: string | null;
  url: string;
  domain: string;
  author: string | null;
  source_type: string | null;
  license: string | null;
  tags: string[];
  why_gem: string | null;
  og_image_url: string | null;
  og_card_url: string | null;
  og_full_url: string | null;
  og_status: string;
  created_at: string | null;
};

type RelatedGem = {
  slug: string;
  name: string;
  description: string | null;
  category: string;
  subcategory: string;
  og_image_url: string | null;
  og_card_url: string | null;
  og_status: string;
  license: string | null;
};

/** Public: fetches a single library item by slug. No auth needed. */
export const getLibraryItemBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }): Promise<LibraryItemRow | null> => {
    const { setResponseHeader } = await import("@tanstack/react-start/server");
    // Public catalogue data: safe to cache at the edge with revalidation.
    setResponseHeader("cache-control", "public, max-age=300, stale-while-revalidate=3600");
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"]!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );
    const res = await (supabase as never as {
      from: (t: string) => {
        select: (c: string) => {
          eq: (k: string, v: string) => {
            maybeSingle: () => Promise<{ data: LibraryItemRow | null }>;
          };
        };
      };
    })
      .from("library_items")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    return res.data;
  });

/** Public: lists related items in the same subcategory (excluding the given slug). */
export const getRelatedGems = createServerFn({ method: "GET" })
  .inputValidator(
    (data: { subcategory: string; excludeSlug: string }) => data,
  )
  .handler(async ({ data }): Promise<RelatedGem[]> => {
    const { setResponseHeader } = await import("@tanstack/react-start/server");
    setResponseHeader("cache-control", "public, max-age=300, stale-while-revalidate=3600");
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"]!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );
    const res = await (supabase as never as {
      from: (t: string) => {
        select: (c: string) => {
          eq: (k: string, v: string) => {
            neq: (k: string, v: string) => {
              limit: (n: number) => Promise<{ data: RelatedGem[] | null }>;
            };
          };
        };
      };
    })
      .from("library_items")
      .select("slug,name,description,category,subcategory,og_image_url,og_card_url,og_status,license")
      .eq("subcategory", data.subcategory)
      .neq("slug", data.excludeSlug)
      .limit(6);
    return res.data ?? [];
  });

/** Public: the first screen of gems, server-rendered so /library paints instantly. */
export const getLibraryFirstPage = createServerFn({ method: "GET" }).handler(
  async (): Promise<import("./library-types").LibraryGem[]> => {
    const { getLibraryFirstPageCached } = await import("./library.server");
    try {
      return await getLibraryFirstPageCached(36);
    } catch {
      return [];
    }
  },
);

/** Scores one batch of previews for visual quality. Admin-only, resumable. */
export const scoreLibraryPreviews = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { limit?: number; reset?: boolean }) => data ?? {})
  .handler(async ({ data, context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { scorePreviewsBatch, resetVisualScores } = await import("./library-og.server");
    if (data?.reset) await resetVisualScores();
    const limit = Math.min(Math.max(data?.limit ?? 60, 1), 120);
    return scorePreviewsBatch(limit);
  });

/** Visual-scoring progress counts. Admin-only. */
export const libraryVisualStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { visualScoreStats } = await import("./library-og.server");
    return visualScoreStats();
  });

/** Pins a gem to the front of the grid (rank 1..n) or clears the pin. Admin-only. */
export const setLibraryFeaturedRank = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { slug: string; rank: number | null }) => data)
  .handler(async ({ data, context }) => {
    const ctx = context as {
      supabase: import("@supabase/supabase-js").SupabaseClient;
      userId: string;
    };
    await assertAdmin(ctx);
    const { setFeaturedRank } = await import("./library-og.server");
    return setFeaturedRank(data.slug, data.rank);
  });
